import { CrudStoreItemPartner360 } from 'legacy/lut/classes/models/partner360/CrudStoreItemPartner360'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { requestCommitmentItems } from 'legacy/lut/features/partner360/commitment'
import { Table } from 'legacy/lut/library/list'
import { restoreAcknowledgement } from 'legacy/lut/services/partner360/commitment/restoreAcknowledgement'
import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'
import { Button } from 'core-components/library/button'
import { Flex } from 'core-components/library/flex'
import { IColumn } from 'core-components/library/list/types'
import { Loader } from 'core-components/library/loader'
import { selectAppOwnerId } from 'core-components/sitebuilder/store/selectors/selectAppOwnerId'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { IFilter, Logic, Operator } from 'core-components/types'
import { CheckAccessHelper } from 'core/classes/helpers/CheckAccessHelper'
import { toFormat } from 'core/classes/helpers/DateHelper'
import { ADMIN } from 'core/constants/roles'
import { CrudStore } from 'core/types/crudStore'
import { ConfirmationDialog } from 'partner360/components/confirmationDialog/ConfirmationDialog'

import { HelpSection } from '../helpButton'
import { ITabProps } from '../p360Tabs'
import { WidgetHeaderWithHelpButton } from '../widgetHeader'

import styles from './CommitmentHistory.module.scss'

interface ICommitmentHistoryProps {
  widgetParams: ITabProps
  isDistributor: boolean
  partnerId: string
  fyear: number
  programType: ProgramTypes
}

export const CommitmentHistory = ({
  widgetParams,
  isDistributor,
  partnerId,
  fyear,
  programType,
}: ICommitmentHistoryProps) => {
  const [sourceUpdateCount, setSourceUpdateCount] = useState(0)
  const [showRestoreConfirmationDialog, setShowRestoreConfirmationDialog] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const ownerId = useSelector(selectAppOwnerId)
  const user = useSelector(selectUser)

  const commitmentHistoryCrud = CrudStore.get(CrudStoreItemPartner360.JBPCommitmentLogView)
  const dispatch = useAppDispatch()

  const isSuperAdmin = useMemo(() => {
    return CheckAccessHelper(user.roleIds, [ADMIN])
  }, [user.roleIds])

  useLayoutEffect(() => {
    if (partnerId && fyear && programType) {
      const filters: (IFilter | null)[] = [
        {
          field: 'PartnerId',
          operator: Operator.EQUAL,
          value: partnerId,
        },
        {
          field: 'FYear',
          operator: Operator.EQUAL,
          value: fyear,
        },
        isDistributor
          ? null
          : {
              field: 'ProgramType',
              operator: Operator.EQUAL,
              value: programType,
            },
      ]

      commitmentHistoryCrud.setDefaultFilter({
        logic: Logic.AND,
        filters: filters.filter((it): it is IFilter => it !== null),
      })

      setSourceUpdateCount((value) => value + 1)
    }
  }, [commitmentHistoryCrud, partnerId, fyear, programType, isDistributor])

  const columns: IColumn[] = [
    {
      field: 'ProgramType',
      title: 'jbp-commitment-history-program-type',
      priority: 1,
    },
    {
      field: 'ActionDate',
      title: 'jbp-commitment-history-action-date',
      priority: 2,
      renderFunction: (item, column) => {
        return (
          <div>{item[column.field] ? toFormat(item[column.field], 'YYYY-MM-DD HH:mm:ss') : ''}</div>
        )
      },
    },
    {
      field: 'UserEmail',
      title: 'jbp-commitment-history-user-email',
      priority: 3,
    },
    {
      field: 'CommitmentType',
      title: 'jbp-commitment-history-commitment-type',
      priority: 4,
    },
    {
      field: 'ActionName',
      title: 'jbp-commitment-history-action-name',
      priority: 5,
    },
    {
      field: 'Comment',
      title: 'jbp-commitment-history-comment',
      priority: 6,
    },
  ]

  const openRestoreAcknowledgementDialog = useCallback(() => {
    setShowRestoreConfirmationDialog(true)
  }, [])

  const closeRestoreAcknowledgementDialog = useCallback(() => {
    setShowRestoreConfirmationDialog(false)
  }, [])

  const onRestore = useCallback(async () => {
    setIsLoading(true)
    closeRestoreAcknowledgementDialog()
    await restoreAcknowledgement({ partnerId, programType, fYear: fyear, ownerId, isDistributor })
    commitmentHistoryCrud.removeQueries(true)
    await dispatch(
      requestCommitmentItems({
        partnerId,
        programType,
        fYear: fyear,
      })
    ).unwrap()
    setIsLoading(false)
  }, [
    closeRestoreAcknowledgementDialog,
    commitmentHistoryCrud,
    dispatch,
    fyear,
    isDistributor,
    ownerId,
    partnerId,
    programType,
  ])

  return (
    <div className={styles.root}>
      <Flex
        className={styles.title}
        justifyContent="space-between"
        alignItems="baseline"
        width="100%"
      >
        <RenderWhen condition={isSuperAdmin}>
          <Button
            className={styles.restoreBtn}
            onClick={openRestoreAcknowledgementDialog}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader dimension={{ height: 20, width: 20 }} />
            ) : (
              <Translation text={'jbp-commitment-restore'} />
            )}
          </Button>
        </RenderWhen>

        <Flex flexGrow={1} justifyContent="center">
          <WidgetHeaderWithHelpButton section={HelpSection.HistoryAcknowledgment}>
            jbp-commitment-history-header
          </WidgetHeaderWithHelpButton>
        </Flex>
      </Flex>

      <Table
        key={'jbp-commitment-history-table'}
        flexWidth={widgetParams.flexWidth}
        header
        columns={columns}
        source={commitmentHistoryCrud}
        sourceUpdateCount={sourceUpdateCount}
        hasTranslate
        pagination
        hasRefresh
      />
      <ConfirmationDialog
        content={'jbp-commitment-restore-confirm'}
        onReject={closeRestoreAcknowledgementDialog}
        onConfirm={onRestore}
        confirmBtnText={'jbp-commitment-restore-confirm-confirm'}
        rejectBtnText={'jbp-commitment-restore-confirm-reject'}
        isVisible={showRestoreConfirmationDialog}
        title={'jbp-commitment-restore-confirm-title'}
      />
    </div>
  )
}
